import React, { useEffect, useState } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import {
  medicalConditionFields,
  medicationFields,
  supplementFields,
} from "../../stories/Forms/fields";
import { medicalConditionsMedicationsSupplementsSchema } from "../../stories/Forms/validationSchema";
import { Row, Col, Spinner } from "react-bootstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import useApis from "../../services/useAPI";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import {
  assessmentStatusUrl,
  assessmentValidateUrl,
  phlebotomyAssessmentStatusUrl,
  phlebotomyAssessementFinalSubmit
} from "../../utils/constants";
import CheckboxGroupForm from "../../stories/Forms/CheckboxGroupForm";
import { XSquare } from "react-bootstrap-icons";
import { useStepDataProtection } from "./useStepDataProtection";
import {
  calculateProgressSteps,
  updateProgressAfterCompletion,
} from "../../utils/utils";

let submissionData;

/**
 * MedicalConditions Component
 * Handles the "Medical Conditions", "Medications", and "Supplements" steps in one form.
 */

function MedicalConditions() {
  const auth = useAuth();
  const { bid, test_type } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const formId = location.pathname.split("/").at(-1);
  const [is_assessment_completed, setIs_assessment_completed] = useState(false);
  const { test_kit_id } = useParams();

  const [data, setData] = useState({});
  const isPhlebotomy = location.pathname.includes("boots");

  const { callApi, resultGet, resultPost, serverError, isLoading } = useApis();

  useStepDataProtection(
    auth?.allStepData,
    resultGet,
    test_kit_id,
    location,
    navigate,
    auth?.showToast,
  );

  // Update progress steps when we get test data
  useEffect(() => {
    if (resultGet) {
      const newProgressSteps = calculateProgressSteps(
        resultGet,
        resultGet?.test_kit_details?.test_kit_type,
        location.pathname
      );
      auth.setProgressSteps(newProgressSteps);
    }
  }, [resultGet, location.pathname]);

  const handleAssessmentSubmit = (values) => {
    // Remove keys with undefined values
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        delete values[key];
      }
    });

    // Medical Conditions logic
    const medicalConditions =
      values?.medical_conditions?.conditions?.condition ||
      auth?.allStepData?.medical_conditions?.condition ||
      [];
    const hasOtherCondition = medicalConditions?.includes("Other");
    const conditionOtherDescription =
      values?.medical_conditions?.other_description ||
      auth?.allStepData?.medical_conditions?.other_description ||
      "";

    // Medications logic
    const medications =
      values?.medications?.medications?.medications ||
      auth?.allStepData?.medications?.medications ||
      [];
    const hasOtherMedications = medications?.includes("Other");
    const medicationOtherDescription =
      values?.medications?.other_description ||
      auth?.allStepData?.medications?.other_description ||
      "";

    // Supplements logic
    const supplements =
      values?.supplements?.supplements?.supplements ||
      auth?.allStepData?.supplements?.supplements ||
      [];
    const hasOtherSupplements = supplements?.includes("Other");
    const supplementOtherDescription =
      values?.supplements?.other_description ||
      auth?.allStepData?.supplements?.other_description ||
      "";

    // Combined submission data
    submissionData = {
      medical_conditions: {
        is_diagnosed: values.medical_conditions?.conditions?.was_diagnosed,
        condition:
          values.medical_conditions?.conditions?.was_diagnosed === "Yes"
            ? medicalConditions
            : [],
        other_description: hasOtherCondition ? conditionOtherDescription : "",
        cancer_type: values?.medical_conditions?.conditions?.cancer_type || "",
        deficiency: values?.medical_conditions?.conditions?.deficiency || "",
      },
      medications: {
        medication: values.medications?.medications?.medication,
        medications:
          values.medications?.medications?.medication === "Yes"
            ? medications
            : [],
        other_description: hasOtherMedications
          ? medicationOtherDescription
          : "",
        specify_medication:
          values.medications?.medications?.specify_medication || "",
      },
      supplements: {
        taking_supplements: values.supplements?.supplements?.taking_supplements,
        supplements:
          values.supplements?.supplements?.taking_supplements === "Yes"
            ? supplements
            : [],
        other_description: hasOtherSupplements
          ? supplementOtherDescription
          : "",
        supplement_details:
          values.supplements?.supplements?.supplement_details || "",
      },
      step: "medical_conditions",
    };

    // Update progress BEFORE API call
    const completedProgressSteps = updateProgressAfterCompletion(
      resultGet,
      resultGet?.test_kit_details?.test_kit_type,
      location.pathname
    );
    auth.setProgressSteps(completedProgressSteps);

    // Make API call
    if (submissionData) {
      callApi(
        {
          user_id: JSON.parse(localStorage.getItem("user_id")),
          ...submissionData,
        },
        `${assessmentValidateUrl}`,
        "POST",
        true,
      );
    }
    
    setData(submissionData);
    auth.setAllStepData((prevStep) => ({ ...prevStep, ...submissionData }));
  };

  useEffect(() => {
    if (!auth?.allStepData && resultGet) {
      auth.setAllStepData(resultGet?.assessment_values);
    }
  }, [resultGet]);

  // Initial API call
  useEffect(() => {
    const fetchAssessmentData = () => {
      if (isPhlebotomy && test_type && bid) {
        callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
      } else if (!isPhlebotomy && !test_type && !bid) {
        if(location?.state?.test_kit_id){
          localStorage.setItem("test_kit_id", location?.state?.test_kit_id);
        }
        callApi(
          null,
          `${assessmentStatusUrl}${
            test_kit_id || localStorage.getItem("test_kit_id")
          }`,
          "GET",
          true,
        );
      }
    };

    fetchAssessmentData();
  }, []);

  useEffect(() => {
    if (resultGet) {
      const getMedicalConditionsValues = () => {
        const medicalConditions =
          resultGet?.assessment_values?.medical_conditions?.condition || [];
        const hasOtherCondition = medicalConditions?.includes("Other");
        const hasCancerCondition = medicalConditions?.includes("Cancer");
        const hasVitaminCondition = medicalConditions?.includes(
          "Vitamin/mineral deficiency",
        );
        const otherDescription =
          resultGet?.assessment_values?.medical_conditions?.other_description ||
          "";
        const cancerTypeDescription =
          resultGet?.assessment_values?.medical_conditions?.cancer_type || "";
        const deficiencyDescription =
          resultGet?.assessment_values?.medical_conditions?.deficiency || "";
        return {
          condition: medicalConditions,
          deficiency: hasVitaminCondition ? deficiencyDescription : "",
          cancer_type: hasCancerCondition ? cancerTypeDescription : "",
          other_description: hasOtherCondition ? otherDescription : "",
          medical_details: hasOtherCondition ? otherDescription : "",
        };
      };

      const getMedicationsValues = () => {
        const medications =
          resultGet?.assessment_values?.medications?.medications || [];
        const hasOtherMedications = medications?.includes("Other");
        const hasCholesterolLoweringMedications = medications?.includes(
          "Cholesterol-lowering",
        );
        const specifyMedication =
          resultGet?.assessment_values?.medications?.specify_medication || "";
        const otherDescription =
          resultGet?.assessment_values?.medications?.other_description || "";
        return {
          specify_medication: hasCholesterolLoweringMedications
            ? specifyMedication
            : "",
          medications: medications,
          medical_other_description: hasOtherMedications
            ? otherDescription
            : "",
          details: hasOtherMedications ? otherDescription : "",
        };
      };

      const getSupplementsValues = () => {
        const supplements =
          resultGet?.assessment_values?.supplements?.supplements || [];
        const hasOtherSupplements = supplements?.includes("Other");
        const hasIronFolateSupplement = supplements?.includes("Iron, Folate");
        const otherDescription =
          resultGet?.assessment_values?.supplements?.other_description || "";
        const supplementDetailsDescription =
          resultGet?.assessment_values?.supplements?.supplement_details || "";
        return {
          supplements: supplements,
          supplement_other_description: hasOtherSupplements
            ? otherDescription
            : "",
          supplement_details: hasIronFolateSupplement
            ? supplementDetailsDescription
            : "",
        };
      };

      const preparedData = {
        medical_conditions: getMedicalConditionsValues(),
        medications: getMedicationsValues(),
        supplements: getSupplementsValues(),
        step: "medical_conditions_medications_supplements",
      };

      setData(preparedData);
    }
  }, [resultGet]);

  // Handle POST result - just navigate
  useEffect(() => {
    if (resultPost) {
      let nextStep;
      if (isPhlebotomy && test_type && bid) {
        callApi(
                { ...auth.allStepData },
                phlebotomyAssessementFinalSubmit(bid),
                "POST",
              );
          navigate(`/bloods/boots/success-page/${test_type}/${bid}`, {
          state: { ...auth.allStepData },
        });
      } else {
        // For regular assessment
        const isSexualHealth =
          resultGet?.test_kit_details?.test_kit_type?.includes("sex") ||
          resultGet?.test_kit_details?.test_kit_type?.includes("menopause") ||
          resultGet?.test_kit_details?.test_kit_type?.includes("polycystic");
        if (isSexualHealth) {
          nextStep = 4;
        } else {
          nextStep = 5;
        }
        navigate(`/assessment/${test_kit_id}/${nextStep}`, {
          state: { ...auth.allStepData },
        });
      }
    }
  }, [resultPost]);

  useEffect(() => {
    setIs_assessment_completed(resultGet?.is_assessment_completed);
    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [resultGet, serverError]);

  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner
          role="output"
          animation="border"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  return (
    <div className="row justify-content-center start-assessment-page">
      <div className="col-md-12">
        <StepProgressBar
          steps={auth.progressSteps.totalSteps}
          currentStep={auth.progressSteps.currentStep}
          completedSteps={auth.progressSteps.completedSteps}
          isCompleted={auth.progressSteps.completedSteps}
          is_assessment_completed={false}
          isLast={auth.progressSteps.totalSteps === 6 || 7}
        />
        <div className="">
          <Row className="justify-content-center">
            <Col md={12} className="p-0">
              <div className="mt-5">
                <CheckboxGroupForm
                  className="assessment-form"
                  fields={[
                    ...medicalConditionFields,
                    ...medicationFields,
                    ...supplementFields,
                  ]}
                  blockRefresh={true}
                  initialValues={{
                    ...data?.medical_conditions,
                    ...data?.medications,
                    ...data?.supplements,
                  }}
                  type={"medical_conditions_medications_supplements"}
                  is_assessment_completed={is_assessment_completed}
                  validationSchema={
                    medicalConditionsMedicationsSupplementsSchema
                  }
                  onSubmit={handleAssessmentSubmit}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

export default MedicalConditions;